import { createSlice } from "@reduxjs/toolkit";
import {
  getUserNotifications,
  markNotificationRead,
  deleteNotification,
  getUnreadNotificationCount,
  markAllNotificationsRead,
} from "../../reducer/notifications/notificationsReducer";
import { isNotificationUnread } from "../../utils/notificationReadState";

import i18n from "../../i18n";
// Read state is driven by the backend `isRead` flag (delivery `status` is a separate,
// FCM-only concept and must NOT be used as the read signal — that caused notifications to
// re-appear as unread after a refetch). The helper now lives in `utils/notificationReadState`
// so the pharmacy panel, which had the same bug, shares one implementation. Re-exported below
// because existing imports point at this slice.
export { isNotificationUnread };

// Initial state
const initialState = {
  // Persistent notifications from API
  notifications: [],
  pagination: {
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
  },
  unreadCount: 0,

  // Loading states
  loading: {
    fetch: false,
    markRead: false,
    delete: false,
    unreadCount: false,
    markAllRead: false,
  },

  // Error states
  error: {
    fetch: null,
    markRead: null,
    delete: null,
    unreadCount: null,
    markAllRead: null,
  },

  // Last fetch timestamp
  lastFetched: null,
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    // Add real-time notification from socket (prevents duplicates)
    addRealtimeNotification: (state, action) => {
      const newNotification = action.payload;
      const existingIndex = state.notifications.findIndex(
        (n) =>
          n.recipient === newNotification.notification_id ||
          n.id === newNotification.id,
      );

      if (existingIndex === -1) {
        // Add new notification at the beginning
        const notification = {
          id: newNotification.id || Date.now(),
          user_id: newNotification.user_id,
          notification_type: newNotification.notification_type,
          status: "sent", // FCM delivery status (not the read state)
          isRead: false, // New real-time notifications start unread
          readAt: null,
          title: newNotification.title || null,
          body: newNotification.body || "",
          // Kept when the event carries them, so the bell can show Arabic for live items.
          title_arabic: newNotification.title_arabic || null,
          body_arabic: newNotification.body_arabic || null,
          recipient: newNotification.notification_id,
          data: newNotification.data || null,
          sent_at: new Date(
            newNotification.timestamp || Date.now(),
          ).toISOString(),
          delivered_at: null,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };

        state.notifications.unshift(notification);
        state.unreadCount += 1;
      }
    },

    // Mark notification as read locally (optimistic update). Matches by the string
    // recipient (real-time notifications) OR the numeric id (persisted push rows).
    markAsReadLocally: (state, action) => {
      const notificationId = action.payload;
      const notification = state.notifications.find(
        (n) =>
          n.recipient === notificationId ||
          String(n.id) === String(notificationId),
      );

      if (notification && isNotificationUnread(notification)) {
        notification.isRead = true;
        notification.readAt = new Date().toISOString();
        state.unreadCount = Math.max(0, state.unreadCount - 1);
      }
    },

    // Remove notification locally (optimistic update)
    removeNotificationLocally: (state, action) => {
      const notificationId = action.payload;
      const index = state.notifications.findIndex(
        (n) =>
          n.recipient === notificationId ||
          String(n.id) === String(notificationId),
      );

      if (index !== -1) {
        const notification = state.notifications[index];
        if (isNotificationUnread(notification)) {
          state.unreadCount = Math.max(0, state.unreadCount - 1);
        }
        state.notifications.splice(index, 1);
      }
    },

    // Clear all notifications
    clearNotifications: (state) => {
      state.notifications = [];
      state.unreadCount = 0;
    },

    // Reset error states
    clearErrors: (state) => {
      state.error = {
        fetch: null,
        markRead: null,
        delete: null,
        unreadCount: null,
        markAllRead: null,
      };
    },
  },
  extraReducers(builder) {
    builder
      // Get user notifications
      .addCase(getUserNotifications.pending, (state) => {
        state.loading.fetch = true;
        state.error.fetch = null;
      })
      .addCase(getUserNotifications.fulfilled, (state, action) => {
        state.loading.fetch = false;

        // Handle null payload or empty payload
        if (!action.payload) {
          console.warn(
            "getUserNotifications: action.payload is null - API returned success but no data",
          );
          // Set empty state
          state.notifications = [];
          state.pagination = {
            total: 0,
            page: 1,
            limit: 20,
            totalPages: 0,
          };
          state.lastFetched = new Date().toISOString();
          state.unreadCount = 0;
          return;
        }

        // Handle different payload structures
        let notifications = [];
        let pagination = null;

        if (
          action.payload.notifications &&
          Array.isArray(action.payload.notifications)
        ) {
          notifications = action.payload.notifications;
          pagination = action.payload.pagination;
        } else if (Array.isArray(action.payload)) {
          // If payload is directly an array of notifications
          notifications = action.payload;
          pagination = {
            total: action.payload.length,
            page: 1,
            limit: 20,
            totalPages: 1,
          };
        } else {
          console.error(
            "getUserNotifications: invalid payload structure",
            action.payload,
          );
          state.error.fetch = i18n.t("toast.invalidResponseFormatFromServer");
          return;
        }

        // Handle case where pagination might be missing
        const validPagination = pagination || {
          total: notifications.length,
          page: 1,
          limit: 20,
          totalPages: Math.ceil(notifications.length / 20),
        };

        // For initial load, replace all notifications; for pagination, merge
        if (validPagination.page === 1) {
          state.notifications = notifications;
        } else {
          // Merge with existing notifications, avoiding duplicates
          const existingIds = new Set(
            state.notifications.map((n) => n.recipient || n.id),
          );
          const newNotifications = notifications.filter(
            (n) => !existingIds.has(n.recipient || n.id),
          );
          state.notifications = [...state.notifications, ...newNotifications];
        }

        state.pagination = validPagination;
        state.lastFetched = new Date().toISOString();

        // Recalculate unread count from the backend read flag (isRead), not delivery status.
        state.unreadCount =
          state.notifications.filter(isNotificationUnread).length;
      })
      .addCase(getUserNotifications.rejected, (state, action) => {
        console.log(
          "getUserNotifications rejected with error:",
          action.payload,
        );
        state.loading.fetch = false;
        state.error.fetch = action.payload;
      })

      // Mark notification as read
      .addCase(markNotificationRead.pending, (state) => {
        state.loading.markRead = true;
        state.error.markRead = null;
      })
      .addCase(markNotificationRead.fulfilled, (state, action) => {
        state.loading.markRead = false;
        // Update is handled by markAsReadLocally reducer
      })
      .addCase(markNotificationRead.rejected, (state, action) => {
        state.loading.markRead = false;
        state.error.markRead = action.payload;
        // Revert optimistic update on error
        // This would need more complex logic to track which notification was being updated
      })

      // Delete notification
      .addCase(deleteNotification.pending, (state) => {
        state.loading.delete = true;
        state.error.delete = null;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        state.loading.delete = false;
        // Update is handled by removeNotificationLocally reducer
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        state.loading.delete = false;
        state.error.delete = action.payload;
        // Revert optimistic update on error
        // This would need more complex logic to track which notification was being deleted
      })

      // Get unread count
      .addCase(getUnreadNotificationCount.pending, (state) => {
        state.loading.unreadCount = true;
        state.error.unreadCount = null;
      })
      .addCase(getUnreadNotificationCount.fulfilled, (state, action) => {
        state.loading.unreadCount = false;

        console.log(
          "getUnreadNotificationCount fulfilled with payload:",
          action.payload,
        );

        // Handle null payload
        if (!action.payload) {
          console.warn(
            "getUnreadNotificationCount: action.payload is null - setting unread count to 0",
          );
          state.unreadCount = 0;
          return;
        }

        // Handle different payload structures
        let unreadCount = 0;

        if (typeof action.payload.unread_count === "number") {
          unreadCount = action.payload.unread_count;
        } else if (typeof action.payload === "number") {
          // If payload is directly a number
          unreadCount = action.payload;
        } else if (
          action.payload.count !== undefined &&
          typeof action.payload.count === "number"
        ) {
          // Alternative field name
          unreadCount = action.payload.count;
        } else {
          console.warn(
            "getUnreadNotificationCount: invalid payload structure, defaulting to 0",
            action.payload,
          );
        }

        state.unreadCount = unreadCount;
      })
      .addCase(getUnreadNotificationCount.rejected, (state, action) => {
        console.log(
          "getUnreadNotificationCount rejected with error:",
          action.payload,
        );
        state.loading.unreadCount = false;
        state.error.unreadCount = action.payload;
      })

      // Mark all as read
      .addCase(markAllNotificationsRead.pending, (state) => {
        state.loading.markAllRead = true;
        state.error.markAllRead = null;
      })
      .addCase(markAllNotificationsRead.fulfilled, (state, action) => {
        state.loading.markAllRead = false;
        // Mark every notification read (isRead) — this is what drives the UI read state.
        const now = new Date().toISOString();
        state.notifications.forEach((notification) => {
          if (isNotificationUnread(notification)) {
            notification.isRead = true;
            notification.readAt = now;
          }
        });
        state.unreadCount = 0;
      })
      .addCase(markAllNotificationsRead.rejected, (state, action) => {
        state.loading.markAllRead = false;
        state.error.markAllRead = action.payload;
      });
  },
});

export const {
  addRealtimeNotification,
  markAsReadLocally,
  removeNotificationLocally,
  clearNotifications,
  clearErrors,
} = notificationsSlice.actions;

// Export async thunks
export {
  getUserNotifications,
  markNotificationRead,
  deleteNotification,
  getUnreadNotificationCount,
  markAllNotificationsRead,
};

export default notificationsSlice.reducer;
